import { tryOnScopeDispose, tryOnMounted } from '@vueuse/core'

import { useUserInfo } from '@/composition/useMember'
import { useLogin } from '@/composition/useLogin'

import { usePlatformStore } from '@/store-pinia/platform'
import type { FmemberMemberInfo } from '@/types/api'

/**
 * 會員頁面登入保護 composable
 * - 未登入時自動打開登入彈窗
 * - 關閉彈窗且未登入時導頁到首頁（funbook 平台除外）
 * - 登入成功後執行回調函數
 * - 組件卸載時自動清理登入狀態
 */
export function useMemberPageAuth(onLoginSuccess?: () => void | Promise<void>) {
  const nuxtApp = useNuxtApp()
  const { $store: store } = nuxtApp

  const platformStore = usePlatformStore()

  const route = useRoute()
  const router = useRouter()
  const { openPureLogin, closeLogin } = useLogin()
  const { userInfo } = useUserInfo()

  const user = computed<FmemberMemberInfo | null>(() => store.getters['authentication/getUser'] || userInfo.value)
  const isLoginDialogShown = computed<boolean>(() => store.getters['dialog/loginDialog'])
  const isBookingEngine = computed<boolean>(() => store.getters.isBookingEngine)
  const isLimitedPlatform = computed(() => isBookingEngine.value || platformStore.isFunBookV2)

  // 監聽用戶登入狀態變化，登入成功後執行回調
  watch(user, async (newUser, oldUser) => {
    if (!oldUser && newUser && typeof onLoginSuccess === 'function') {
      await onLoginSuccess()
    }
  })

  // 監聽登入彈窗關閉，關閉且未登入時導頁（非 funbook）
  watch(isLoginDialogShown, newIsLoginDialogShown => {
    if (!newIsLoginDialogShown && !user.value && !isLimitedPlatform.value) {
      router.replace(nuxtApp.$localePath({ path: '/', query: route.query }))
    }
  })

  tryOnMounted(() => {
    if (import.meta.server) {
      return
    }

    requestAnimationFrame(() => {
      if (!user.value) {
        openPureLogin()
      }
    })
  })

  tryOnScopeDispose(() => {
    closeLogin()
  })
}
